import { ChangeDetectionStrategy, Component, HostListener, OnInit, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { PageTitleService } from '../../shared/services';
import { Professional, Service } from '../../shared/interfaces';
import { AgendaService, BusinessService } from '../../services/business';
import { BaseAuthComponent } from '../../shared/common/BaseAuthComponent.class';
import { AnalyticsService } from '../../services/analytics.service';
import { AnalyticsResponse } from '../../shared/interfaces/analytics.interface';
import { AnalyticsFilters } from './components/analytics-filters/analytics-filters';
import { AnalyticsSummary } from './components/analytics-summary/analytics-summary';
import { RevenueChart } from './components/charts/revenue-chart';
import { StatusBreakdown } from './components/status-breakdown/status-breakdown';
import { RankedList, RankedRow } from './components/ranked-list/ranked-list';
import { BusyHours } from './components/busy-hours/busy-hours';
import { AppointmentsTable } from './components/appointments-table/appointments-table';
import { ReportPanel } from './components/report-panel/report-panel';
import { SummaryDrawer } from './components/summary-drawer/summary-drawer';
import { ReportFilterState } from './utils/report-context';
import { formatCurrency, formatNumber, formatPercent } from './utils/report-format';

// Chart.js registration
import { Chart, registerables } from 'chart.js';
Chart.register(...registerables);

type ServiceMode = 'revenue' | 'count';

@Component({
  selector: 'app-reports',
  imports: [
    AnalyticsFilters,
    AnalyticsSummary,
    RevenueChart,
    StatusBreakdown,
    RankedList,
    BusyHours,
    AppointmentsTable,
    ReportPanel,
    SummaryDrawer,
  ],
  templateUrl: './reports.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Reports extends BaseAuthComponent implements OnInit {
  private readonly titleService = inject(PageTitleService);
  private readonly agendaService = inject(AgendaService);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly businessService = inject(BusinessService);
  private readonly router = inject(Router);

  readonly analytics = signal<AnalyticsResponse | null>(null);
  readonly loading = signal(false);
  readonly loadError = signal(false);
  readonly filterState = signal<ReportFilterState | null>(null);
  readonly exporting = signal<'pdf' | 'xlsx' | null>(null);
  readonly exportMenuOpen = signal(false);
  readonly serviceMode = signal<ServiceMode>('revenue');
  readonly summaryOpen = signal(false);
  /** La guía de inicio usa la misma esquina: si está visible, el botón flotante sube */
  readonly liftFab = signal(false);

  readonly professionals = signal<{ id: string; name: string }[]>([]);
  readonly services = signal<{ id: string; name: string }[]>([]);

  /** Propietario y administradores ven al equipo completo; un profesional solo sus datos */
  readonly canSeeTeam = this.authService.isOwner() || this.authService.isSystemAdmin();
  readonly businessName = this.authService.getBusinessName() || 'Mi negocio';

  readonly serviceRows = computed<RankedRow[]>(() => {
    const items = this.analytics()?.charts.topServices ?? [];
    const byCount = this.serviceMode() === 'count';
    return [...items]
      .sort((a, b) => (byCount ? b.count - a.count : b.revenue - a.revenue))
      .map((s) => ({
        id: s.id,
        name: s.name,
        value: byCount ? s.count : s.revenue,
        valueLabel: byCount ? `${formatNumber(s.count)} citas` : formatCurrency(s.revenue),
        detail: byCount
          ? `${formatNumber(s.completedCount)} completadas, ${formatCurrency(s.revenue)}`
          : `${formatNumber(s.count)} citas, ${formatNumber(s.completedCount)} completadas`,
      }));
  });

  readonly teamRows = computed<RankedRow[]>(() =>
    (this.analytics()?.charts.professionalPerformance ?? []).map((p) => ({
      id: p.id,
      name: p.name,
      value: p.revenue,
      valueLabel: formatCurrency(p.revenue),
      detail: `${formatNumber(p.appointmentCount)} citas, ${formatPercent(p.completionRate)} de asistencia`,
    })),
  );

  readonly paymentRows = computed<RankedRow[]>(() => {
    const items = this.analytics()?.charts.revenueByPaymentMethod ?? [];
    const total = items.reduce((s, m) => s + m.value, 0);
    return items.map((m) => ({
      id: m.label,
      name: m.label,
      value: m.value,
      valueLabel: formatCurrency(m.value),
      detail: `${formatPercent(total ? (m.value / total) * 100 : 0, 1)} de lo cobrado`,
    }));
  });

  readonly clientRows = computed<RankedRow[]>(() =>
    (this.analytics()?.charts.clientAnalytics.topClients ?? []).map((c) => ({
      id: c.id,
      name: c.name,
      value: c.totalSpent,
      valueLabel: formatCurrency(c.totalSpent),
      detail: `${formatNumber(c.visitCount)} ${c.visitCount === 1 ? 'visita' : 'visitas'}`,
      tag: c.isNew ? 'Nuevo' : undefined,
    })),
  );

  readonly clientSplit = computed(() => {
    const ca = this.analytics()?.charts.clientAnalytics;
    if (!ca) return null;
    const total = ca.newCount + ca.returningCount;
    return {
      total,
      newCount: ca.newCount,
      returningCount: ca.returningCount,
      newShare: total ? (ca.newCount / total) * 100 : 0,
    };
  });

  ngOnInit(): void {
    const breadcrumbs = this.router.url.startsWith('/settings/reports')
      ? [{ name: 'Configuración', url: '/settings' }]
      : [];
    this.titleService.setPage({
      title: 'Informes',
      subtitle: 'Cómo van tus citas, servicios y ventas',
      breadcrumbs,
    });
    this.loadFilterOptions();
  }

  onFiltersChanged(state: ReportFilterState): void {
    this.filterState.set(state);
    this.loadAnalytics();
  }

  loadAnalytics(): void {
    const state = this.filterState();
    if (!state) return;
    this.loading.set(true);
    this.loadError.set(false);
    this.analyticsService.getAnalytics(state.request).subscribe({
      next: (res) => {
        this.liftFab.set(!!document.querySelector('.welcome-pill'));
        // Si el usuario cambió los filtros mientras cargaba, se ignora la respuesta vieja
        if (this.filterState() !== state) return;
        this.analytics.set(res);
        this.loading.set(false);
      },
      error: (err) => {
        console.error('Error loading analytics:', err);
        if (this.filterState() !== state) return;
        this.loading.set(false);
        this.loadError.set(true);
      },
    });
  }

  openSummary(): void {
    this.exportMenuOpen.set(false);
    this.summaryOpen.set(true);
  }

  closeSummary(): void {
    this.summaryOpen.set(false);
    this.liftFab.set(!!document.querySelector('.welcome-pill'));
  }

  downloadPdfFromSummary(): void {
    this.summaryOpen.set(false);
    this.export('pdf');
  }

  toggleExportMenu(event: Event): void {
    event.stopPropagation();
    this.exportMenuOpen.update((v) => !v);
  }

  @HostListener('document:click')
  @HostListener('document:keydown.escape')
  closeExportMenu(): void {
    this.exportMenuOpen.set(false);
  }

  async export(kind: 'pdf' | 'xlsx'): Promise<void> {
    const state = this.filterState();
    const analytics = this.analytics();
    if (!state || !analytics || this.exporting()) return;

    this.exportMenuOpen.set(false);
    this.exporting.set(kind);
    this.preloader.show();
    try {
      const res = await firstValueFrom(
        this.agendaService.getEventsForReports({
          startDate: state.request.startDate,
          endDate: state.request.endDate,
          professionalId: state.request.professionalId,
          serviceId: state.request.serviceId,
          clientId: state.request.clientId,
          statusCode: state.request.statusCode,
          page: 1,
          pageSize: 10000,
          sortBy: 'startDate',
          sortOrder: 'asc',
        }),
      );
      const { exportReportExcel, exportReportPdf } = await import('./utils/report-export');
      const input = {
        businessName: this.businessName,
        rangeStart: state.rangeStart,
        rangeEnd: state.rangeEnd,
        filters: state.filters,
        analytics,
        events: res.events ?? [],
        includeTeam: this.canSeeTeam,
      };
      if (kind === 'pdf') await exportReportPdf(input);
      else await exportReportExcel(input);
      this.toastServiceBase.show(kind === 'pdf' ? 'Informe PDF descargado' : 'Informe de Excel descargado', 'success');
    } catch (error) {
      console.error('Error exporting report:', error);
      this.toastServiceBase.show(
        kind === 'pdf' ? 'No se pudo generar el PDF. Intenta de nuevo.' : 'No se pudo generar el Excel. Intenta de nuevo.',
        'error',
      );
    } finally {
      this.exporting.set(null);
      this.preloader.hide();
    }
  }

  private loadFilterOptions(): void {
    if (this.canSeeTeam) {
      this.businessService
        .getRecordsByBusinessId<Professional>(this.businessId, { page: 1, pageSize: 100, isActive: true }, 'professionals')
        .subscribe({
          next: (res) =>
            this.professionals.set(
              (res.data || []).map((p) => ({ id: p.userId, name: `${p.firstName} ${p.lastName}`.trim() })),
            ),
        });
    }

    this.businessService
      .getRecordsByBusinessId<Service>(this.businessId, { page: 1, pageSize: 100 }, 'services')
      .subscribe({
        next: (res) => this.services.set((res.data || []).map((s) => ({ id: s.serviceId, name: s.name }))),
      });
  }
}
